import { useEffect, useRef } from "react";
import { cn } from "../../lib/format";
import { useLang } from "../../i18n";
import { TURNSTILE_SITE_KEY as SITE_KEY } from "../../lib/turnstile";

/**
 * Cloudflare Turnstile ("Verifying…" box) for the sign-up forms. The token it
 * returns is checked by the backend (VTCNODE middleware/turnstile.js).
 *
 * .env: VITE_TURNSTILE_SITE_KEY (Cloudflare dashboard → Turnstile → your widget →
 * Site key; add your domains and localhost to the widget's hostnames).
 * Without it nothing is shown and the forms work as before (local development).
 * Test site key that always passes: 1x00000000000000000000AA
 */

interface TurnstileApi {
  render: (el: HTMLElement, options: Record<string, unknown>) => string;
  reset: (id: string) => void;
  remove: (id: string) => void;
}
declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let script: Promise<TurnstileApi> | null = null;
const loadTurnstile = () =>
  (script ??= new Promise<TurnstileApi>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const el = document.createElement("script");
    el.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    el.async = true;
    el.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile unavailable")));
    el.onerror = () => {
      script = null; // allow a retry on the next mount
      reject(new Error("Turnstile script failed to load"));
    };
    document.head.appendChild(el);
  }));

interface TurnstileProps {
  /** The token once the check passes, null when it expires or fails. */
  onToken: (token: string | null) => void;
  /** Change it to get a fresh token (tokens work once: reset after a failed submit). */
  resetKey?: number;
  className?: string;
}

export default function Turnstile({ onToken, resetKey = 0, className }: TurnstileProps) {
  const lang = useLang();
  const box = useRef<HTMLDivElement>(null);
  const widget = useRef<string | null>(null);
  const callback = useRef(onToken);
  useEffect(() => {
    callback.current = onToken;
  });

  // Render once per language (the widget's text follows the site language)
  useEffect(() => {
    if (!SITE_KEY || !box.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((ts) => {
        if (cancelled || !box.current) return;
        widget.current = ts.render(box.current, {
          sitekey: SITE_KEY,
          language: lang,
          theme: "light",
          size: "flexible",
          callback: (token: string) => callback.current(token),
          "expired-callback": () => callback.current(null),
          "error-callback": () => callback.current(null),
        });
      })
      .catch(() => callback.current(null));
    return () => {
      cancelled = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
      callback.current(null);
    };
  }, [lang]);

  useEffect(() => {
    if (!resetKey || !widget.current) return;
    window.turnstile?.reset(widget.current);
    callback.current(null);
  }, [resetKey]);

  if (!SITE_KEY) return null;
  return <div ref={box} className={cn("min-h-[65px]", className)} />;
}
